import {isNumber} from 'lodash/fp';
import {ReactNode, useCallback, useMemo} from 'react';

import {UserAmsRole, UserFilterInput, UserIdAndSourceCodes} from 'modules/decisionsNext/api/types';
import {FilterTreeNode} from 'modules/decisionsNext/components/BaseFilter/types';
import {UseTreeData} from 'modules/decisionsNext/hooks/trees/types';

import {FeatureFlags} from 'core/constants/featureFlags';
import {ProducerFilterKeys, TeamsFilterKeys} from 'core/constants/policyFilter';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {useAccountManagersTree} from 'modules/decisionsNext/hooks/trees/userTrees/useAccountManagersTree';
import {getInternalId, getOriginalId, useAllUsers} from 'modules/decisionsNext/hooks/trees/userTrees/useUserTree';
import {
  GenericTreeNode,
  getAllLeaveKeys,
  groupSelectedKeys,
  hasChildren,
  iterateTree,
  ungroupSelectedKeys,
} from 'utils/treeUtils';
import {useProjectName} from 'utils/hooks/useProjectName';

import {SkipQueryParams, StoreAdapterParams, TreeBasicNode} from 'components/Tree/TreeTypes';
import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';

export type ProducerTreeOptions = {
  filter?: UserFilterInput;
  skip?: boolean;
  forProducerFilterSelection?: boolean;
  useAmsCodes?: boolean;
};

export function useProducerTree(options?: ProducerTreeOptions) {
  const {filter, skip, forProducerFilterSelection, useAmsCodes = true} = options || {};
  const {data, loading} = useAllUsers(filter, skip);
  const withAmsCodes = useFeatureFlag(FeatureFlags.AmsProducerCodes) && useAmsCodes;

  const treeData = useMemo(() => {
    return [
      {
        key: 'all',
        title: 'All Producers',
        children: data?.users.paginated.items
          .filter(item => item.amsRoles.includes(UserAmsRole.Producer))
          .map(item => ({
            key: forProducerFilterSelection ? getInternalId({userId: item.id, role: UserAmsRole.Producer}) : item.id,
            title: item.fullName,
            ...(withAmsCodes
              ? {
                  children: item?.shortNames?.map(shortName => ({
                    key: getInternalId({userId: item.id, amsCodeId: shortName, role: UserAmsRole.Producer}),
                    title: shortName,
                  })),
                }
              : {}),
          })),
      },
    ];
  }, [data?.users.paginated.items, forProducerFilterSelection, withAmsCodes]);

  const treeMap = useMemo(() => {
    const map = new Map<string, TreeBasicNode>();

    iterateTree(
      treeData,
      (node, parent) => {
        map.set(node.key, {
          key: node.key,
          title: node.title,
          parentKey: parent?.key,
          childrenCount: hasChildren(node) ? node.children.length : undefined,
        });
      },
      {withParent: true}
    );

    return map;
  }, [treeData]);

  return {
    treeMap,
    treeData,
    loading,
  };
}

export type UserTreeMap = Record<
  string,
  {
    title: ReactNode;
    children?: Array<{
      key: string;
      title: ReactNode;
    }>;
  }
>;

export function transformUserTreeIntoMap(tree: GenericTreeNode<TreeBasicNode>[]): UserTreeMap {
  return tree.reduce(
    (
      acc: Record<string, {title: ReactNode; children?: Array<{key: string; title: ReactNode}>}>,
      {key, title, children}
    ) => {
      const [userId] = getOriginalId(key);

      acc[userId] = {
        title,
        children: children ?? undefined,
      };

      return acc;
    },
    {}
  );
}

export const useProducerTreeSeparated: UseTreeData = ({skip}: SkipQueryParams = {}) => {
  const {data, loading} = useAllUsers({}, skip);
  const withAmsCodes = useFeatureFlag(FeatureFlags.AmsProducerCodes);
  const isAccountExecutivesAvailable = useFeatureFlag(FeatureFlags.AccountExecutives);
  const {isMga} = useProjectName();

  const {accountManagers, producers, accountExecutive} = useMemo(() => {
    const producers: FilterTreeNode[] = [];
    const accountManagers: FilterTreeNode[] = [];
    const accountExecutive: FilterTreeNode[] = [];

    data?.users.paginated.items.forEach(item => {
      if (item.amsRoles.includes(UserAmsRole.Producer)) {
        producers.push({
          key: getInternalId({userId: item.id, role: UserAmsRole.Producer}),
          title: item.fullName,
          ...(withAmsCodes
            ? {
                children: item?.shortNames?.map(shortName => ({
                  key: getInternalId({userId: item.id, amsCodeId: shortName, role: UserAmsRole.Producer}),
                  title: shortName,
                })),
              }
            : {}),
        });
      }

      if (item.amsRoles.includes(UserAmsRole.AccountManager)) {
        accountManagers.push({
          key: getInternalId({userId: item.id, role: UserAmsRole.AccountManager}),
          title: item.fullName,
          ...(withAmsCodes
            ? {
                children: item?.shortNames?.map(shortName => ({
                  key: getInternalId({userId: item.id, amsCodeId: shortName, role: UserAmsRole.AccountManager}),
                  title: shortName,
                })),
              }
            : {}),
        });
      }

      if (item.amsRoles.includes(UserAmsRole.AccountExecutive) && isAccountExecutivesAvailable) {
        accountExecutive.push({
          key: getInternalId({userId: item.id, role: UserAmsRole.AccountExecutive}),
          title: item.fullName,
          ...(withAmsCodes
            ? {
                children: item?.shortNames?.map(shortName => ({
                  key: getInternalId({userId: item.id, amsCodeId: shortName, role: UserAmsRole.AccountExecutive}),
                  title: shortName,
                })),
              }
            : {}),
        });
      }
    });

    return {
      producers,
      accountManagers,
      accountExecutive,
    };
  }, [data?.users.paginated.items, withAmsCodes, isAccountExecutivesAvailable]);

  const treeData = useMemo<GenericTreeNode<TreeBasicNode>[]>(() => {
    return [
      {
        key: 'all-producers',
        title: 'All Producers',
        children: producers,
      },
      {
        key: 'all-account-managers',
        title: 'All Account Managers',
        children: accountManagers,
      },
      {
        key: 'all-account-executives',
        title: 'All Account Executives',
        children: accountExecutive,
      },
    ].filter(section => {
      if (section.key === 'all-account-executives' && (!isAccountExecutivesAvailable || isMga)) {
        return false;
      }

      if (section.key === 'all-account-managers' && isMga) {
        return false;
      }

      return true;
    });
  }, [accountManagers, producers, accountExecutive, isMga, isAccountExecutivesAvailable]);

  return {
    treeData,
    treeMap: useMemo(() => {
      const map = new Map<string, TreeBasicNode>();

      iterateTree(
        treeData,
        (node, parent) => {
          map.set(node.key, {key: node.key, title: node.title, parentKey: parent?.key});
        },
        {
          withParent: true,
        }
      );

      return map;
    }, [treeData]),
    loading,
  };
};

export function useProducerFilterSelection(
  treeData: GenericTreeNode<TreeBasicNode>[],
  {store, setStore}: StoreAdapterParams<ProducerFilterKeys>
) {
  const {userFilters} = store;

  const producerTreeMap = useMemo(() => {
    return transformUserTreeIntoMap(treeData || []);
  }, [treeData]);

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const newSelectedMap = newSelected.reduce((acc, internalId) => {
        const [userId, amsCodeId] = getOriginalId(internalId);

        if (!acc.has(userId)) {
          acc.set(userId, amsCodeId ? [amsCodeId] : []);
        } else if (amsCodeId) {
          acc.get(userId)?.push(amsCodeId);
        }

        return acc;
      }, new Map<string, string[]>());

      const newSelectedWithOriginalIds = Array.from(newSelectedMap, ([userId, sourceCodes]) => ({
        userId,
        ...(sourceCodes.length && sourceCodes.length !== producerTreeMap[userId]?.children?.length
          ? {sourceCodes}
          : {}),
      }));

      setStore({
        userFilters: {
          producerIdsAndSourceCodes: newSelectedWithOriginalIds.length ? newSelectedWithOriginalIds : undefined,
        },
      });
    },
    [producerTreeMap, setStore]
  );

  const setSelectedNone = useCallback(() => {
    setStore({
      userFilters: {
        producerIdsAndSourceCodes: undefined,
      },
    });
  }, [setStore]);

  const selectedProducers = useMemo(() => {
    const internalIds = (userFilters?.producerIdsAndSourceCodes || [])?.reduce((acc: string[], id) => {
      const sameUserIds =
        id.sourceCodes?.map(item => getInternalId({userId: id.userId, amsCodeId: item, role: UserAmsRole.Producer})) ||
        [];

      if (sameUserIds.length) {
        acc.push(...sameUserIds);
      } else {
        acc.push(getInternalId({userId: id.userId, role: UserAmsRole.Producer}));
      }

      return acc;
    }, []);

    return Array.from(ungroupSelectedKeys(treeData, internalIds));
  }, [treeData, userFilters?.producerIdsAndSourceCodes]);

  return useOptimizedSelection({
    selected: selectedProducers,
    setSelected,
    setSelectedNone,
    treeData,
    groupSelection: true,
  });
}

export const getIdsWithSourceCodes = (selected: string[], map: UserTreeMap) => {
  const selectedMap = selected.reduce((acc, internalId) => {
    const [userId, amsCodeId] = getOriginalId(internalId);

    if (!acc.has(userId)) {
      acc.set(userId, amsCodeId ? [amsCodeId] : []);
    } else if (amsCodeId) {
      acc.get(userId)?.push(amsCodeId);
    }

    return acc;
  }, new Map<string, string[]>());

  return Array.from(selectedMap, ([userId, sourceCodes]) => ({
    userId,
    ...(sourceCodes.length && sourceCodes.length !== map[userId]?.children?.length ? {sourceCodes} : {}),
  }));
};

export function getUserFiltersTreeEntitySelection(
  tree: ReturnType<typeof useProducerTree | typeof useAccountManagersTree>['treeData'],
  selected: string[]
) {
  const treeMap = transformUserTreeIntoMap(tree);
  const grouped = groupSelectedKeys(tree, selected);
  return getIdsWithSourceCodes(Array.from(grouped), treeMap);
}

const getSelectedIds = (
  idsAndSourceCodes: UserIdAndSourceCodes[],
  treeData: GenericTreeNode<TreeBasicNode>[],
  role: UserAmsRole
) => {
  const internalIds = (idsAndSourceCodes || [])?.reduce((acc: string[], id) => {
    const sameUserIds = id.sourceCodes?.map(item => getInternalId({userId: id.userId, amsCodeId: item, role})) || [];

    if (sameUserIds.length) {
      acc.push(...sameUserIds);
    } else {
      acc.push(getInternalId({userId: id.userId, role}));
    }

    return acc;
  }, []);

  return Array.from(ungroupSelectedKeys(treeData, internalIds));
};

export function useUserTreeSelection(
  treeData: GenericTreeNode<TreeBasicNode>[],
  {
    store,
    setStore,
    isProducersDisabled,
    isAccountManagersDisabled,
    isAccountExecutivesDisabled,
  }: StoreAdapterParams<TeamsFilterKeys> & {
    isProducersDisabled?: boolean;
    isAccountManagersDisabled?: boolean;
    isAccountExecutivesDisabled?: boolean;
  }
) {
  const {userFilters} = store;

  const setProducersSelectedNone = useCallback(() => {
    setStore({
      userFilters: {
        producerIdsAndSourceCodes: undefined,
      },
    });
  }, [setStore]);

  const setAccountManagersSelectedNone = useCallback(() => {
    setStore({
      userFilters: {
        accountManagerIdsAndSourceCodes: undefined,
      },
    });
  }, [setStore]);

  const setAccountExecutivesSelectedNone = useCallback(() => {
    setStore({
      userFilters: {
        accountExecutiveIdsAndSourceCodes: undefined,
      },
    });
  }, [setStore]);

  const producersTreeData = useMemo(() => {
    return treeData.find(item => item.key === 'all-producers')?.children ?? [];
  }, [treeData]);

  const accountManagersTreeData = useMemo(() => {
    return treeData.find(item => item.key === 'all-account-managers')?.children ?? [];
  }, [treeData]);

  const accountExecutivesTreeData = useMemo(() => {
    return treeData.find(item => item.key === 'all-account-executives')?.children ?? [];
  }, [treeData]);

  const producerTreeMap = useMemo(() => {
    return transformUserTreeIntoMap(producersTreeData);
  }, [producersTreeData]);

  const accountManagersTreeMap = useMemo(() => {
    return transformUserTreeIntoMap(accountManagersTreeData);
  }, [accountManagersTreeData]);

  const accountExecutivesTreeMap = useMemo(() => {
    return transformUserTreeIntoMap(accountExecutivesTreeData);
  }, [accountExecutivesTreeData]);

  const selectedAccountManagers = useMemo(() => {
    return getSelectedIds(
      userFilters?.accountManagerIdsAndSourceCodes || [],
      accountManagersTreeData,
      UserAmsRole.AccountManager
    );
  }, [accountManagersTreeData, userFilters?.accountManagerIdsAndSourceCodes]);

  const selectedAccountExecutives = useMemo(() => {
    return getSelectedIds(
      userFilters?.accountExecutiveIdsAndSourceCodes || [],
      accountExecutivesTreeData,
      UserAmsRole.AccountExecutive
    );
  }, [accountExecutivesTreeData, userFilters?.accountExecutiveIdsAndSourceCodes]);

  const selectedProducers = useMemo(() => {
    return getSelectedIds(userFilters?.producerIdsAndSourceCodes || [], producersTreeData, UserAmsRole.Producer);
  }, [producersTreeData, userFilters?.producerIdsAndSourceCodes]);

  const producersSelection = useOptimizedSelection({
    setSelected: useCallback(
      (newSelected: string[]) => {
        const idsWithSourceCodes = getIdsWithSourceCodes(newSelected, producerTreeMap);

        setStore({
          userFilters: {
            producerIdsAndSourceCodes: idsWithSourceCodes.length ? idsWithSourceCodes : undefined,
          },
        });
      },
      [producerTreeMap, setStore]
    ),
    selected: selectedProducers,
    setSelectedNone: setProducersSelectedNone,
    treeData: producersTreeData,
  });

  const accountManagersSelection = useOptimizedSelection({
    setSelected: useCallback(
      (newSelected: string[]) => {
        const idsWithSourceCodes = getIdsWithSourceCodes(newSelected, accountManagersTreeMap);

        setStore({
          userFilters: {
            accountManagerIdsAndSourceCodes: idsWithSourceCodes.length ? idsWithSourceCodes : undefined,
          },
        });
      },
      [accountManagersTreeMap, setStore]
    ),
    selected: selectedAccountManagers,
    setSelectedNone: setAccountManagersSelectedNone,
    treeData: accountManagersTreeData,
  });

  const accountExecutivesSelection = useOptimizedSelection({
    setSelected: useCallback(
      (newSelected: string[]) => {
        const idsWithSourceCodes = getIdsWithSourceCodes(newSelected, accountExecutivesTreeMap);

        setStore({
          userFilters: {
            accountExecutiveIdsAndSourceCodes: idsWithSourceCodes.length ? idsWithSourceCodes : undefined,
          },
        });
      },
      [accountExecutivesTreeMap, setStore]
    ),
    selected: selectedAccountExecutives,
    setSelectedNone: setAccountExecutivesSelectedNone,
    treeData: accountExecutivesTreeData,
  });

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const {producerIds, accountManagerIds, accountExecutivesIds} = splitTeams(newSelected);

      producersSelection.setSelected(producerIds);
      accountManagersSelection.setSelected(accountManagerIds);
      accountExecutivesSelection.setSelected(accountExecutivesIds);
    },
    [accountManagersSelection, producersSelection, accountExecutivesSelection]
  );

  const disabledKeys = useMemo(() => {
    const accountManagersDisabledKeys = getAllLeaveKeys(accountManagersTreeData);
    const producersDisabledKeys = getAllLeaveKeys(producersTreeData);
    const accountExecutivesDisabledKeys = getAllLeaveKeys(accountExecutivesTreeData);

    return [
      ...(isAccountManagersDisabled ? accountManagersDisabledKeys : []),
      ...(isProducersDisabled ? producersDisabledKeys : []),
      ...(isAccountExecutivesDisabled ? accountExecutivesDisabledKeys : []),
    ];
  }, [
    accountManagersTreeData,
    producersTreeData,
    accountExecutivesTreeData,
    isAccountManagersDisabled,
    isProducersDisabled,
    isAccountExecutivesDisabled,
  ]);

  const selected = useMemo(() => {
    return [
      ...producersSelection.selected,
      ...accountManagersSelection.selected,
      ...accountExecutivesSelection.selected,
    ];
  }, [producersSelection.selected, accountManagersSelection.selected, accountExecutivesSelection.selected]);

  const selectionCount =
    producersSelection.selectionCount === 'All' &&
    accountManagersSelection.selectionCount === 'All' &&
    accountExecutivesSelection.selectionCount === 'All'
      ? 'All'
      : (isNumber(producersSelection.selectionCount) && !isProducersDisabled ? producersSelection.selectionCount : 0) +
        (isNumber(accountManagersSelection.selectionCount) && !isAccountManagersDisabled
          ? accountManagersSelection.selectionCount
          : 0) +
        (isNumber(accountExecutivesSelection.selectionCount) && !isAccountExecutivesDisabled
          ? accountExecutivesSelection.selectionCount
          : 0);

  return {
    selected,
    selectedProducers,
    selectedAccountManagers,
    selectedAccountExecutives,
    producersTreeData,
    accountManagersTreeData,
    accountExecutivesTreeData,
    selectionCount,
    selectedNoneProducers: {
      userFilters: {
        producerIdsAndSourceCodes: undefined,
      },
    },
    selectedNoneAccountManagers: {
      userFilters: {
        accountManagerIdsAndSourceCodes: undefined,
      },
    },
    selectedNoneAccountExecutives: {
      userFilters: {
        accountExecutiveIdsAndSourceCodes: undefined,
      },
    },
    disabledKeys,
    setSelected,
    setProducersSelected: producersSelection.setSelected,
    setAccountManagersSelected: accountManagersSelection.setSelected,
    setAccountExecutivesSelected: accountExecutivesSelection.setSelected,
    setProducersSelectedNone,
    setAccountManagersSelectedNone,
    setAccountExecutivesSelectedNone,
    getInternalId,
    getOriginalId,
    producerTreeMap,
    accountManagersTreeMap,
    accountExecutivesTreeMap,
  };
}

function splitTeams(selected: string[]) {
  const producerIds: string[] = [];
  const accountManagerIds: string[] = [];
  const accountExecutivesIds: string[] = [];

  selected.forEach(id => {
    const [, , role] = getOriginalId(id);

    switch (role) {
      case UserAmsRole.Producer:
        producerIds.push(id);
        break;
      case UserAmsRole.AccountManager:
        accountManagerIds.push(id);
        break;
      case UserAmsRole.AccountExecutive:
        accountExecutivesIds.push(id);
        break;
    }
  });

  return {producerIds, accountManagerIds, accountExecutivesIds};
}
